import React, { FC, useCallback, useState, useContext, useEffect, useMemo } from 'react'

import { makeStyles } from '@mui/styles'
import { Grid, Theme } from '@mui/material'

import { FormProvider, useForm } from 'react-hook-form'

import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom'
import { toast } from 'react-toastify'

import { getPossibleStatusLabelsForCrm } from '@sparling/financial-common'
import { endOfDay, startOfDay } from 'date-fns'
import { columnsFinanceTransaction, columnsUserTransaction } from './transactionsList'

import { DataPicker } from '../../../components/DatePicker'
import { Search } from '../../../components/Search'
import { TableComponent } from '../../../components/Table'
import { FilterSelect } from '../../../components/FilterSelect'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { icons } from '../../../constants/icons'
import { CRM_TRANSACTION_TYPE } from '../../../constants/CrmTransactionType'
import { MemoAddTransaction } from './AddTransaction'
import { MemoStatusesTransactions } from './EditTransaction'
import { routes } from '../../../constants/routes'
import { AppStateContext, useRefresh, useRest } from '../../../components/Context'
import { IModalAsset } from '../../Settings/Assets/Staking'
import useDebounce from '../../../hooks/useDebounce'
import { createEnum } from '../../../utils/createEnum'
import { generateParams } from '../../../utils'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(4),
    '& p': {
      display: 'none',
    },
  },
  search: {
    marginRight: (isFinancePage) => (isFinancePage ? 0 : 'auto'),
  },
  rightSide: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: 'max-content',
  },
  title: {
    lineHeight: 0,
  },
  button: {
    paddingLeft: 15,
  },
  table: {
    paddingTop: theme.spacing(1),
  },
}))

interface UserTransactionsProps {
  userId?: string | undefined
}

export const UserTransactions: FC<UserTransactionsProps> = ({ userId }) => {
  const history = useHistory()
  const location = useLocation()
  const isFinancePage = location.pathname.includes(routes.FINANCE)
  const isCompanyPage = !!useRouteMatch(routes.COMPANIES)
  const classes = useStyles(isFinancePage)
  const { tab } = useParams<{ tab: string }>()

  const { rxios } = useRest()
  const { getIsRefresh } = useContext(AppStateContext)
  const { saveIsRefresh } = useRefresh()
  const { search } = useLocation()
  const [loading, setLoading] = useState<boolean>(false)
  const [isInitDate, setInitDate] = useState<boolean>(false)
  // eslint-disable-next-line
  const [values, setValues] = useState<any>({})
  // eslint-disable-next-line
  const [startDateSelected, setStartDateSelected] = useState<any>('')
  // eslint-disable-next-line
  const [endDateSelected, setEndDateSelected] = useState<any>('')
  // eslint-disable-next-line
  const [transactionStatus, setTransactionStatus] = useState<any>('')
  // eslint-disable-next-line
  const [transactionType, setTransactionType] = useState<any>('')
  const [queryString, setQueryString] = useState<string>('')
  const [searchDebounce, setSearchDebounce] = useState<string>('')
  const debouncedValue = useDebounce<string>(queryString, 200)

  const [isOpenModalAddTransaction, setOpenAddTransaction] = useState<boolean>(false)
  const [isOpenModalStatusesTransactions, setOpenStatusesTransactions] = useState<boolean>(false)
  const [editId, setEditId] = useState<number | undefined>()
  const [refresh, setRefresh] = useState<boolean | undefined>()

  useEffect(() => setRefresh(getIsRefresh()), [setRefresh, getIsRefresh])

  const { getUserRole } = useContext(AppStateContext)
  const [userRole, setUserRole] = useState<string>()

  useEffect(() => setUserRole(getUserRole()), [setUserRole, getUserRole])

  const formMethods = useForm({
    mode: 'onBlur',
  })
  const { watch, getValues } = formMethods

  const toggleModalAddTransaction = useCallback(
    () => setOpenAddTransaction((prevState) => !prevState),
    [setOpenAddTransaction],
  )

  const toggleModalStatusesTransactions = useCallback(
    (id?: number | string) => {
      setEditId(typeof id === 'number' ? id : undefined)
      setOpenStatusesTransactions((prevState) => !prevState)
    },
    [setOpenStatusesTransactions],
  )

  const singleData = useMemo(() => {
    return editId && values.items.find((elem: IModalAsset) => elem.id === editId)
    // eslint-disable-next-line
  }, [editId])

  const stringParams = new URLSearchParams(search)
  const pageUrl: number | string | null = stringParams.get('page')
  // eslint-disable-next-line
  useEffect(() => setSearchDebounce(queryString), [debouncedValue])

  const pushParamsToURL = useCallback(
    (startDate, endDate, status, type, searchString) => {
      const currentUrlParams = new URLSearchParams(window.location.search)

      if (startDate) {
        currentUrlParams.set('startDate', startDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('startDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (endDate) {
        currentUrlParams.set('endDate', endDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('endDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (status) {
        currentUrlParams.set('status', status)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('status')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }

      if (type) {
        currentUrlParams.set('type', type)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('type')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }

      if (searchString) {
        currentUrlParams.set('search', searchString)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('search')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }
    },
    [history],
  )

  const loadData = useCallback(
    async (page, startDate = '', endDate = '', status = '', type = '', searchString = '') => {
      let cleanupFunction = false
      setLoading(true)

      const generatedParams = generateParams({
        page,
        count: '10',
        startDate,
        endDate,
        status,
        type,
        search: searchString.toUpperCase(),
        ...(isCompanyPage && { companyId: userId }),
      })

      rxios
        .get(isFinancePage ? `transaction?${generatedParams}` : `transaction-by-user/${userId}?${generatedParams}`)
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setValues(next)
            const currentPath = window.location.pathname
            if (currentPath.includes('/finance/')) {
              history.push(`/finance/transactions?page=${page}`)
            } else if (currentPath.includes('/companies/')) {
              history.push(`${currentPath.split('?')[0]}?page=${page}`)
            } else {
              history.push(`${currentPath.split('?')[0]}?page=${page}`)
            }

            pushParamsToURL(startDate, endDate, status, type, searchString)

            if (!cleanupFunction) setLoading(false)
          },
          (error) => {
            pushParamsToURL(startDate, endDate, status, type, searchString)
            toast.error(error)
            setLoading(false)
          },
          () => {
            setLoading(false)
            saveIsRefresh(false)
          },
        )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [],
  )

  const initData = useCallback(
    async (page, startDate = '', endDate = '', status = '', type = '', searchString = '') => {
      let cleanupFunction = false
      setLoading(true)

      const generatedParams = generateParams({
        page,
        count: '10',
        startDate,
        endDate,
        status,
        type,
        search: searchString.toUpperCase(),
        ...(isCompanyPage && { companyId: userId }),
      })

      rxios
        .get(isFinancePage ? `transaction?${generatedParams}` : `transaction-by-user/${userId}?${generatedParams}`)
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setValues(next)
            if (!cleanupFunction) setLoading(false)
            setInitDate(true)
            const currentPath = window.location.pathname
            if (currentPath.includes('/finance/')) {
              history.push(`/finance/transactions?page=${page}`)
            } else if (currentPath.includes('/companies/')) {
              history.push(`${currentPath.split('?')[0]}?page=${page}`)
            } else {
              history.push(`${currentPath.split('?')[0]}?page=${page}`)
            }
          },
          (error) => {
            toast.error(error)
            setLoading(false)
          },
          () => {
            setLoading(false)
          },
        )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [rxios],
  )

  useEffect(() => {
    ;(async () => {
      await initData(pageUrl || '1')
    })()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'startDate') setStartDateSelected(value?.startDate?.toISOString())
      if (name === 'endDate') setEndDateSelected(value?.endDate?.toISOString())
      if (name === 'transactionStatus') setTransactionStatus(getValues().transactionStatus)
      if (name === 'transactionType') setTransactionType(getValues().transactionType.toLowerCase())
      if (name === 'search') setQueryString(value?.[name])
    })
    return () => subscription.unsubscribe()
  }, [watch, getValues])

  useEffect(() => {
    if (isInitDate) {
      setEndDateSelected(endDateSelected === '1970-01-01' ? '' : endDateSelected)
      ;(async () => {
        await loadData(
          1,
          startDateSelected || '',
          endDateSelected || '',
          transactionStatus || '',
          transactionType || '',
          searchDebounce || '',
        )
      })()
    }
    // eslint-disable-next-line
  }, [startDateSelected, endDateSelected, transactionStatus, transactionType, searchDebounce])

  useEffect(() => {
    if (refresh && isInitDate) {
      ;(async () => {
        await loadData(
          pageUrl,
          startDateSelected || '',
          endDateSelected || '',
          transactionStatus || '',
          transactionType || '',
          searchDebounce || '',
        )
      })()
    }
    // eslint-disable-next-line
  }, [refresh, isInitDate])

  const toggleTransaction = useCallback(
    (id: string | number) => {
      history.push(`${tab}/${id}/details`)
    },
    [history, tab],
  )

  const isAdminCrm = useMemo(() => {
    return userRole === 'admin_crm'
  }, [userRole])

  return (
    <>
      <Grid container className={classes.topWrap}>
        <FormProvider {...formMethods}>
          <Grid container className={classes.rightSide}>
            <DataPicker
              placeholder={'Start Date'}
              margin="8px 8px 8px 0"
              dataPickerName={'startDate'}
              required={false}
              maxDate={getValues().endDate}
              formatValue={startOfDay}
            />
            <DataPicker
              placeholder={'End Date'}
              margin={8}
              dataPickerName={'endDate'}
              required={false}
              minDate={getValues().startDate}
              formatValue={endOfDay}
            />
            <FilterSelect
              inputName={'transactionStatus'}
              placeholder="Status"
              enums={createEnum(getPossibleStatusLabelsForCrm())}
              width={138}
              ml={8}
              mr={8}
            />
            <FilterSelect
              inputName={'transactionType'}
              placeholder="Type"
              enums={CRM_TRANSACTION_TYPE}
              width={138}
              ml={8}
              mr={16}
            />
          </Grid>
          <Search pr={16} pl={0} inputName={'search'} className={classes.search} />
        </FormProvider>

        {!isFinancePage && isAdminCrm && (
          <GreenButton
            width={160}
            prefix={icons.ADD}
            ml={0}
            text={'Add Transaction'}
            onClick={toggleModalAddTransaction}
          />
        )}
      </Grid>

      <Grid container className={classes.table}>
        <TableComponent
          getElementId={toggleTransaction}
          columns={isFinancePage ? columnsFinanceTransaction : columnsUserTransaction}
          data={values.items}
          params={values.meta}
          loadData={(pageNumber: number | string | null) =>
            loadData(
              pageNumber,
              startDateSelected || '',
              endDateSelected || '',
              transactionStatus || '',
              transactionType || '',
              searchDebounce || '',
            )
          }
          isShowInfoIcon={false}
          isShowEditIcon={isAdminCrm}
          onEdit={toggleModalStatusesTransactions}
          loading={loading}
        />
      </Grid>

      {!isFinancePage && (
        <MemoAddTransaction isOpen={isOpenModalAddTransaction} modalClose={toggleModalAddTransaction} userId={userId} />
      )}

      <MemoStatusesTransactions
        isOpen={isOpenModalStatusesTransactions}
        modalClose={toggleModalStatusesTransactions}
        singleData={singleData}
      />
    </>
  )
}
